import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
} from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import { useEffect } from "react";
import { auth } from "./lib/firebase";
import { fetchCurrentUser } from "./lib/auth";
import { onAuthStateChanged } from "firebase/auth";
import {
  LEGACY_PUBLIC_MATCH_SHARE_ROUTE_PATH,
  PUBLIC_MATCH_SHARE_ROUTE_PATH,
} from "./lib/shareAnalytics";
import { resolveHomeRouteOutcome } from "./lib/homeRouteGate";

const IS_E2E_TEST_MODE = import.meta.env.VITE_E2E_TEST_MODE === "true";

// Layout
import Layout from "./components/Layout.tsx";

// Pages
import Login from "./pages/Login.tsx";
import Dashboard from "./pages/Dashboard.tsx";
import AdminDashboard from "./pages/AdminDashboard.tsx";
import LoggerCockpit from "./pages/LoggerCockpit.tsx";
// ...
// <Route path=":matchId/logger" element={<div>Logger Disabled</div>} />
import TeamDetail from "./pages/TeamDetail.tsx";
import MatchDetail from "./pages/MatchDetail.tsx";
import LiveMatch from "./pages/LiveMatch.tsx";
import NotFound from "./pages/NotFound.tsx";
import GuestWaiting from "./pages/GuestWaiting.tsx";

// Admin CRUD Pages
import UsersManager from "./pages/UsersManager.tsx";
import CompetitionsManager from "./pages/CompetitionsManager.tsx";
import VenuesManager from "./pages/VenuesManager.tsx";
import RefereesManager from "./pages/RefereesManager.tsx";
import PlayersManager from "./pages/PlayersManager.tsx";
import TeamsManager from "./pages/TeamsManager.tsx";
import MatchesManager from "./pages/MatchesManager.tsx";
import IngestionManager from "./pages/IngestionManager.tsx";
import IngestionPage from "./pages/IngestionPage.tsx";
import RosterImportPage from "./pages/RosterImportPage.tsx";
import AdminModelConfig from "./pages/AdminModelConfig.tsx";
import AiSettingsPage from "./pages/AiSettingsPage.tsx";
import ConflictsView from "./pages/ConflictsView.tsx";
import ReferenceDataManager from "./pages/ReferenceDataManager.tsx";
import ShareSettingsPage from "./pages/admin/ShareSettingsPage.tsx";
import BroadcastersPage from "./pages/admin/BroadcastersPage.tsx";

// Components
import ProtectedRoute from "./components/ProtectedRoute.tsx";
import OfflineIndicator from "./components/OfflineIndicator.tsx";
import AuthLoadingScreen from "./components/AuthLoadingScreen.tsx";

// Public (unauthenticated) pages
import PublicMatchAnalytics from "./pages/PublicMatchAnalytics.tsx";
// Broadcast overlay (EPIC-108): token-authenticated, no app chrome.
import BroadcastPage from "./pages/broadcast/BroadcastPage.tsx";
import ControlPage from "./pages/broadcast/ControlPage.tsx";
import Home from "./pages/Home.tsx";

// Public marketing homepage gate (EPIC-38). `/` used to be the first child
// of the protected layout route below, so any visitor — authenticated or
// not — hit ProtectedRoute first and an unauthenticated one was bounced to
// /login before ever seeing a homepage. This component now owns bare "/"
// directly, as a sibling of the protected layout route (which dropped its
// own "/" path — see the router definition). The decision itself lives in
// `resolveHomeRouteOutcome` (src/lib/homeRouteGate.ts, unit-pinned there);
// this just renders whichever of its three outcomes applies.
function HomeRoute() {
  const { user, loading } = useAuthStore();
  const outcome = resolveHomeRouteOutcome(user, loading);

  switch (outcome.kind) {
    case "loading":
      return <AuthLoadingScreen />;
    case "redirect":
      return <Navigate to={outcome.to} replace />;
    case "home":
      return <Home />;
  }
}

// Router is defined at module scope so it is never recreated on re-renders
// or onAuthStateChanged state changes (which would cause router remounts and
// lost navigation state / transient socket disconnects).
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route path="/login" element={<Login />} />

      {/* Public analytics — no authentication required */}
      <Route
        path={PUBLIC_MATCH_SHARE_ROUTE_PATH}
        element={<PublicMatchAnalytics />}
      />
      <Route
        path={LEGACY_PUBLIC_MATCH_SHARE_ROUTE_PATH}
        element={<PublicMatchAnalytics />}
      />

      {/* Public marketing homepage — no authentication required. Claims
          bare "/" exclusively; the protected layout route below is now
          pathless (see its comment) so the two never both match "/". */}
      <Route path="/" element={<HomeRoute />} />

      {/* Broadcast link (EPIC-108): public, token-in-path pages for OBS and
          the producer. Auth is the broadcast/control token, not Firebase. */}
      <Route path="/broadcast/control/:token" element={<ControlPage />} />
      <Route path="/broadcast/:token" element={<BroadcastPage />} />

      {/* Guest waiting page - accessible to guests and admin */}
      <Route
        path="/guest"
        element={
          <ProtectedRoute allowedRoles={["guest", "admin"]}>
            <GuestWaiting />
          </ProtectedRoute>
        }
      />

      {/* Pathless layout route (no `path`): contributes no URL segment of
          its own, so its children's relative paths ("dashboard", "admin",
          ...) resolve to the same absolute paths ("/dashboard", "/admin",
          ...) as before. Previously this route owned "/" itself with an
          `index` child that navigated to /dashboard — that's now HomeRoute
          above, so an unauthenticated visitor to "/" never reaches this
          ProtectedRoute (and is never bounced to /login for it). */}
      <Route
        element={
          <ProtectedRoute>
            <>
              <OfflineIndicator />
              <Layout />
            </>
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />

        {/* Admin-only routes */}
        <Route
          path="admin"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <UsersManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/ingestion"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <IngestionManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/ingestion/:batchId"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <IngestionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/ingestion/config"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminModelConfig />
            </ProtectedRoute>
          }
        />
        {/* AI roster importer (EPIC-95) — replaces the CSV/JSON player
            import cards in /admin/ingestion; see IngestionManager.tsx. */}
        <Route
          path="admin/roster-import"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <RosterImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/conflicts"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <ConflictsView />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/ai-settings"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AiSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/share-settings"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <ShareSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/broadcasters"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <BroadcastersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/reference-data"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <ReferenceDataManager />
            </ProtectedRoute>
          }
        />

        {/* Data Management Routes - accessible to admin & analyst */}
        <Route path="competitions" element={<CompetitionsManager />} />
        <Route path="venues" element={<VenuesManager />} />
        <Route path="referees" element={<RefereesManager />} />
        <Route path="players" element={<PlayersManager />} />

        <Route path="teams">
          <Route index element={<TeamsManager />} />
          <Route path=":teamId" element={<TeamDetail />} />
        </Route>

        <Route path="matches">
          <Route index element={<MatchesManager />} />
          <Route path=":matchId" element={<MatchDetail />} />
          <Route path=":matchId/live" element={<LiveMatch />} />
          <Route path=":matchId/logger" element={<LoggerCockpit />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>,
  ),
);

function App() {
  const { setUser, setLoading } = useAuthStore();

  useEffect(() => {
    if (IS_E2E_TEST_MODE) {
      // E2E tests can opt into a logged-out state (to exercise the login page)
      // by setting this flag before the app boots. Test-only; no prod effect.
      let forceLoggedOut = false;
      try {
        forceLoggedOut =
          window.localStorage.getItem("e2e-force-logged-out") === "true";
      } catch {
        forceLoggedOut = false;
      }
      // E2E mode bootstraps an analyst user without Firebase (unless forced out).
      setUser(
        forceLoggedOut
          ? null
          : {
              uid: "e2e-user",
              email: "e2e@example.com",
              displayName: "E2E Analyst",
              photoURL: "",
              role: "analyst",
            },
      );
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          await fetchCurrentUser();
          await firebaseUser.getIdToken(true);
        } catch (error) {
          console.error("Failed to sync backend session", error);
        }

        const idTokenResult = await firebaseUser.getIdTokenResult();
        const role = (idTokenResult.claims.role as string) || "guest";

        // Map old roles to new roles if needed
        const roleMapping: Record<string, "admin" | "analyst" | "guest"> = {
          Admin: "admin",
          Logger: "analyst",
          Viewer: "guest",
          admin: "admin",
          analyst: "analyst",
          guest: "guest",
        };

        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email || "",
          displayName: firebaseUser.displayName || "",
          photoURL: firebaseUser.photoURL || "",
          role: roleMapping[role] || "guest",
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [setUser, setLoading]);

  return <RouterProvider router={router} />;
}

export default App;
